import { readTerminalCredential, type TrustedTerminalIdentity } from "../local-store/terminal-state";
import { internTerminalIdentity } from "../local-store/terminal-identity-intern";

export type GatewayClientOptions = {
  baseUrl: string;
  timeoutMs?: number;
};

export type EnrollTerminalInput = {
  organizationCode: string;
  username: string;
  password: string;
  nodeCode: string;
  action: "ISSUE" | "ROTATE";
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type EnrollmentResult = {
  ok: boolean;
  contract: string;
  result_status: string;
  organization_id: string;
  node_id: string;
  branch_id: string;
  commerce_channel_id: string;
  node_code: string;
  node_name: string;
  credential_id: string;
  credential_version: number;
  credential_status: string;
  request_key: string;
  secret_available: boolean;
  device_secret?: string | null;
};

export type LoginEmployeeInput = {
  deviceSecret: string;
  employeeCode: string;
  pin: string;
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type EmployeeRuntimeActor = {
  ok: boolean;
  authenticated: boolean;
  contract: string;
  organization_id: string;
  node_id: string;
  node_code: string;
  branch_id: string;
  branch_code: string;
  branch_name: string;
  commerce_channel_id: string;
  employee_id: string;
  employee_code: string;
  employee_name: string;
  employee_credential_version: number;
  device_credential_id: string;
  device_credential_version: number;
  permission_codes: string[];
  runtime_session_id: string;
  runtime_session_status: "ACTIVE" | "ENDED";
  runtime_session_issued_at: string;
  runtime_session_verified_at?: string;
};

export type EmployeeLoginResult = EmployeeRuntimeActor & {
  runtime_session_token: string;
  request_key: string;
  idempotent_replay?: boolean;
};

export type ResumeEmployeeSessionInput = {
  deviceSecret: string;
  sessionToken: string;
};

export type EndEmployeeSessionInput = {
  deviceSecret: string;
  sessionToken: string;
  endReason: "EXPLICIT_LOGOUT" | "FAST_SWITCH";
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type EndEmployeeSessionResult = {
  ok: boolean;
  contract: string;
  organization_id: string;
  branch_id: string;
  node_id: string;
  employee_id: string;
  runtime_session_id: string;
  runtime_session_status: "ENDED";
  end_reason: string;
  ended_at: string | null;
  request_key: string;
  idempotent_replay?: boolean;
};

export type CashShiftSnapshot = {
  shift_id: string;
  shift_status: "OPENING" | "OPEN" | "CLOSING" | "CLOSED";
  organization_id: string;
  branch_id: string;
  node_id: string;
  responsible_employee_id: string;
  responsible_employee_code: string;
  responsible_employee_name: string;
  opening_cash: number | null;
  opened_at: string | null;
  open_request_key: string;
  expected_cash: number | null;
  counted_cash: number | null;
  cash_difference: number | null;
  payment_summary: Record<string, unknown> | null;
  void_total: number | null;
  refund_total: number | null;
  cash_in_total: number | null;
  cash_out_total: number | null;
  closed_by_employee_id: string | null;
  closed_at: string | null;
};

export type CashShiftRuntime = {
  ok: boolean;
  contract: string;
  terminal: TrustedTerminalIdentity;
  active_shift: CashShiftSnapshot | null;
  has_active_shift: boolean;
  idempotent_replay?: boolean;
  shift_id?: string;
};

export type ShiftOpeningCarryLine = {
  denomination_code: string;
  denomination_value: number;
  denomination_kind: string;
  display_label: string;
  quantity: number;
  amount: number;
};

export type ShiftOpeningDenomination = {
  denomination_code: string;
  denomination_value: number;
  denomination_kind: string;
  display_label: string;
  sort_order: number;
};

export type ShiftOpeningCarryForward = {
  source_shift_id: string;
  source_count_confirmation_event_id: string;
  source_closed_at: string | null;
  currency_code: string;
  suggested_amount: number;
  lines: ShiftOpeningCarryLine[];
  recount_denominations: ShiftOpeningDenomination[];
};

export type CashShiftOpeningRuntime = {
  ok: boolean;
  contract: "GET_POS_CASH_SHIFT_OPENING_RUNTIME_V1" | string;
  active_shift: CashShiftSnapshot | null;
  has_active_shift: boolean;
  opening_carry_forward: ShiftOpeningCarryForward | null;
};

export type OpenCashShiftInput = {
  deviceSecret: string;
  employeeCode: string;
  pin: string;
  openingCash: number;
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type OpenCashShiftV2Input = {
  deviceSecret: string;
  sessionToken: string;
  employeeCode: string;
  pin: string;
  openingMode: "MANUAL" | "CARRY_FORWARD_CONFIRM" | "RECOUNT";
  sourceShiftId?: string | null;
  openingCash?: number | null;
  openingLines?: Array<{ denomination_code: string; quantity: number }> | null;
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type CashShiftOpeningResult = {
  opening_mode: "MANUAL" | "CARRY_FORWARD_CONFIRM" | "RECOUNT";
  opening_source_shift_id?: string | null;
  currency_code?: string;
  suggested_amount?: number;
  actual_amount: number;
  opening_variance?: number;
  opening_lines?: ShiftOpeningCarryLine[];
};

export type OpenCashShiftV2Result = CashShiftRuntime & {
  opened?: boolean;
  opening_result?: CashShiftOpeningResult;
};

export type GatewayResult<T> = {
  ok: boolean;
  data?: T;
  error?: string;
  authenticated?: boolean;
};

async function reclassifySupersededDeviceCredential<T>(
  deviceSecret: string,
  result: GatewayResult<T>,
): Promise<GatewayResult<T>> {
  if (result.ok) return result;

  // Multiple POS tabs/app instances share the durable terminal trust store but each
  // JavaScript instance can still hold an older in-memory Device Secret after ROTATE.
  // An invalid response for that superseded secret must never be treated as authority
  // to delete the employee session belonging to the newer persisted credential.
  const current = await readTerminalCredential();
  if (current?.deviceSecret && current.deviceSecret !== deviceSecret) {
    return { ...result, error: "TERMINAL_CREDENTIAL_SUPERSEDED" };
  }

  return result;
}

export class PosIdentityGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: GatewayClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  enrollTerminal(input: EnrollTerminalInput) {
    return this.post<EnrollmentResult>("/api/pos/identity/enroll", {
      organization_code: input.organizationCode,
      username: input.username,
      password: input.password,
      node_code: input.nodeCode,
      action: input.action,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  async resolveTerminal(deviceSecret: string) {
    const rawResult = await this.post<TrustedTerminalIdentity>("/api/pos/identity/resolve", {
      device_secret: deviceSecret,
    });
    const result = await reclassifySupersededDeviceCredential(deviceSecret, rawResult);
    if (!result.ok || !result.data) return result;
    return { ...result, data: internTerminalIdentity(result.data) };
  }

  loginEmployee(input: LoginEmployeeInput) {
    return this.post<EmployeeLoginResult>("/api/pos/identity/login", {
      device_secret: input.deviceSecret,
      employee_code: input.employeeCode,
      pin: input.pin,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  async resumeEmployeeSession(input: ResumeEmployeeSessionInput) {
    const result = await this.post<EmployeeRuntimeActor>("/api/pos/identity/session/resolve", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
    });
    return reclassifySupersededDeviceCredential(input.deviceSecret, result);
  }

  endEmployeeSession(input: EndEmployeeSessionInput) {
    return this.post<EndEmployeeSessionResult>("/api/pos/identity/session/end", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      end_reason: input.endReason,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  getCashShiftRuntime(deviceSecret: string) {
    return this.post<CashShiftRuntime>("/api/pos/shift/runtime", {
      device_secret: deviceSecret,
    });
  }

  getCashShiftOpeningRuntime(deviceSecret: string, sessionToken: string) {
    return this.post<CashShiftOpeningRuntime>("/api/pos/shift/opening-runtime", {
      device_secret: deviceSecret,
      session_token: sessionToken,
    });
  }

  openCashShift(input: OpenCashShiftInput) {
    return this.post<CashShiftRuntime>("/api/pos/shift/open", {
      device_secret: input.deviceSecret,
      employee_code: input.employeeCode,
      pin: input.pin,
      opening_cash: input.openingCash,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  openCashShiftV2(input: OpenCashShiftV2Input) {
    return this.post<OpenCashShiftV2Result>("/api/pos/shift/open-v2", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      employee_code: input.employeeCode,
      pin: input.pin,
      opening_mode: input.openingMode,
      source_shift_id: input.sourceShiftId ?? null,
      opening_cash: input.openingCash ?? null,
      opening_lines: input.openingLines ?? null,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  private async post<T>(path: string, body: Record<string, unknown>): Promise<GatewayResult<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
        credentials: "omit",
      });

      const payload = (await response.json()) as GatewayResult<T>;
      if (!response.ok) {
        return {
          ok: false,
          authenticated: payload.authenticated,
          error: payload.error ?? `GATEWAY_HTTP_${response.status}`,
        };
      }

      return payload;
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof DOMException && error.name === "AbortError"
            ? "GATEWAY_TIMEOUT"
            : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
